import React, { useCallback, useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    IconButton,
    Paper,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import moment from 'moment'
import AuthService from 'tools/AuthService'
import { Toast } from 'components/shared/Toaster'
import {
    getMonitoringRecipients,
    updateMonitoringRecipients,
    getMonitoringNotificationAudit,
    getMonitoringRecipientCandidates,
} from 'actions/monitoring'

const EMAIL_RE = /^\S+@\S+\.\S+$/

// We only have the recipient's email (no stored name/role), so derive a friendly display name and initials
// from the address' local part — e.g. "marcus.delgado@…" -> "Marcus Delgado" / "MD". Best-effort only.
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s)
const displayName = (email) => {
    const local = (email || '').split('@')[0]
    const parts = local.split(/[._+-]+/).filter(Boolean)
    if (!parts.length) return email || ''
    return parts.slice(0, 2).map(titleCase).join(' ')
}
const initialsOf = (email) => {
    const parts = displayName(email).split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return (parts[0] || email || '?').slice(0, 2).toUpperCase()
}
const AVATAR_COLORS = ['#7c6cf0', '#2b3a37', '#4f7d6b', '#a061e0', '#3f6f8f']
const avatarColor = (email) => {
    let h = 0
    for (let i = 0; i < (email || '').length; i += 1) h = (h * 31 + email.charCodeAt(i)) >>> 0
    return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

// Group the (newest-first) audit rows into consecutive day buckets for a scannable history.
const dayLabel = (m) => {
    const today = moment().startOf('day')
    const d = m.clone().startOf('day')
    if (d.isSame(today)) return `Today · ${m.format('MMM DD, YYYY')}`
    if (d.isSame(today.clone().subtract(1, 'day'))) return `Yesterday · ${m.format('MMM DD, YYYY')}`
    return m.format('dddd · MMM DD, YYYY')
}
const groupAuditByDay = (list) => {
    const groups = []
    let current = null
    list.forEach((a) => {
        const m = a.sentAt ? moment(a.sentAt) : null
        const key = m ? m.clone().startOf('day').valueOf() : 'unknown'
        if (!current || current.key !== key) {
            current = { key, label: m ? dayLabel(m) : 'Unknown date', items: [] }
            groups.push(current)
        }
        current.items.push(a)
    })
    return groups
}

// Client-admin management of who receives monitoring alert digest emails, and the send audit. Anyone with
// monitoring can view; only MANAGE_CLIENT_NOTIFICATION_SETTINGS (CUSTOMER_ADMIN / ROX_ADMIN) can edit
// recipients — matching the server-side gate.
const MonitoringNotifications = ({ history }) => {
    const canManage = AuthService.hasPrivilege('MANAGE_CLIENT_NOTIFICATION_SETTINGS')

    const [rows, setRows] = useState([])       // current server recipients [{id?, email, enabled}]
    const [newEmail, setNewEmail] = useState('') // the picked user email to add
    const [candidates, setCandidates] = useState([]) // this client's user emails, for the picker
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [audit, setAudit] = useState([])
    const [auditLoading, setAuditLoading] = useState(false)

    const loadRecipients = useCallback(() => {
        setLoading(true)
        getMonitoringRecipients()()
            .then((res) => setRows(Array.isArray(res) ? res.map((r) => ({ ...r })) : []))
            .catch(() => Toast({ type: 'error', message: 'Failed to load recipients' }))
            .finally(() => setLoading(false))
    }, [])

    const loadAudit = useCallback(() => {
        setAuditLoading(true)
        getMonitoringNotificationAudit()()
            .then((res) => setAudit(Array.isArray(res) ? res : []))
            .catch(() => Toast({ type: 'error', message: 'Failed to load notification history' }))
            .finally(() => setAuditLoading(false))
    }, [])

    // The client's user emails for the picker. Only admins (canManage) can edit recipients, so only they
    // need it; kept off the non-admin path so nobody hits an endpoint they can't use.
    const loadCandidates = useCallback(() => {
        if (!canManage) return
        getMonitoringRecipientCandidates()()
            .then((res) => setCandidates(Array.isArray(res) ? res.filter(Boolean) : []))
            .catch(() => { /* picker just shows no options; non-fatal */ })
    }, [canManage])

    useEffect(() => { loadRecipients(); loadAudit(); loadCandidates() }, [loadRecipients, loadAudit, loadCandidates])

    // Persist a whole recipient list (the API is a full replace). The UI updates optimistically and
    // reconciles with the saved list; on failure it reloads from the server so nothing gets out of sync.
    const persist = (nextRows, successMessage) => {
        setSaving(true)
        setRows(nextRows)
        updateMonitoringRecipients(nextRows.map((r) => ({ id: r.id, email: r.email, enabled: !!r.enabled })))()
            .then((res) => {
                setRows(Array.isArray(res) ? res.map((r) => ({ ...r })) : [])
                if (successMessage) Toast({ type: 'success', message: successMessage })
            })
            .catch((err) => {
                Toast({ type: 'error', message: err?.response?.data?.message || 'Failed to save recipients' })
                loadRecipients()
            })
            .finally(() => setSaving(false))
    }

    const addRow = () => {
        const email = newEmail.trim()
        if (!email) return
        if (!EMAIL_RE.test(email)) { Toast({ type: 'error', message: 'Enter a valid email address' }); return }
        if (rows.some((r) => (r.email || '').toLowerCase() === email.toLowerCase())) {
            Toast({ type: 'error', message: `${email} is already in the list` }); return
        }
        setNewEmail('')
        persist([...rows, { email, enabled: true }], `${email} added`)
    }

    // Toggling enabled saves silently; add/remove show a brief confirmation.
    const toggle = (index, value) => persist(rows.map((r, i) => (i === index ? { ...r, enabled: value } : r)))
    const remove = (index) => {
        const removed = rows[index]
        persist(rows.filter((_, i) => i !== index), removed ? `${removed.email} removed` : undefined)
    }

    // Users not already on the recipient list — the pickable options.
    const availableCandidates = candidates.filter(
        (c) => !rows.some((r) => (r.email || '').toLowerCase() === c.toLowerCase()))
    const enabledCount = rows.filter((r) => r.enabled).length
    const auditGroups = groupAuditByDay(audit)

    const openApplicant = (applicationId) => { if (applicationId && history) history.push(`/applicant/${applicationId}`) }

    return (
        <div className='monitoringNotifications'>
            <Paper variant='outlined' style={{ overflow: 'hidden', marginBottom: 32 }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: 20 }}>
                    <div>
                        <h5 className='mb-0' style={{ fontWeight: 700 }}>Notification recipients</h5>
                        <div style={{ fontSize: 13, color: '#5b6b68', marginTop: 6, maxWidth: 560, lineHeight: 1.5 }}>
                            These people get monitoring alerts for every business. To get alerts for just one
                            business, open its monitoring settings and turn on Notify me.{' '}
                            {canManage ? '' : 'Only a customer admin can edit this list.'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                        {saving && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5b6b68' }}>
                                <CircularProgress size={14} /> Saving…
                            </span>
                        )}
                    </div>
                </div>

                {/* Add a recipient */}
                {canManage && (
                    <div style={{ padding: '16px 20px', borderTop: '1px solid #eef1f0', background: '#fafbfb' }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Add a recipient</div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                            <TextField
                                select
                                size='small'
                                variant='outlined'
                                label='Choose a user'
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                style={{ minWidth: 340 }}
                                disabled={saving || availableCandidates.length === 0}
                                helperText={candidates.length === 0
                                    ? 'No users found for this client'
                                    : (availableCandidates.length === 0
                                        ? 'All users are already recipients'
                                        : 'Only users of this client can be added. Free-text addresses are not accepted.')}
                            >
                                {availableCandidates.map((email) => (
                                    <MenuItem key={email} value={email}>{email}</MenuItem>
                                ))}
                            </TextField>
                            <Button
                                startIcon={<AddIcon />}
                                variant='contained'
                                color='primary'
                                size='small'
                                style={{ marginTop: 2 }}
                                disabled={saving || !newEmail}
                                onClick={addRow}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                )}

                {/* Count summary */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', padding: '10px 20px',
                    borderTop: '1px solid #eef1f0', fontSize: 12, letterSpacing: '.04em',
                    color: '#5b6b68', fontWeight: 600, textTransform: 'uppercase',
                }}>
                    <span>{rows.length} recipient{rows.length === 1 ? '' : 's'}</span>
                    <span>{enabledCount} enabled</span>
                </div>

                {/* Recipient list */}
                {loading && !rows.length ? (
                    <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: '32px 20px', textAlign: 'center', color: '#5b6b68' }}>
                        <div style={{ fontWeight: 600, color: '#1f2a28', marginBottom: 4 }}>No extra recipients yet</div>
                        <div style={{ fontSize: 13 }}>
                            Whoever created each monitoring schedule is still notified.
                            {canManage ? ' Add a teammate above to include them too.' : ''}
                        </div>
                    </div>
                ) : (
                    rows.map((row, index) => (
                        <div
                            key={row.id ?? row.email}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
                                borderTop: '1px solid #f3f5f4', opacity: row.enabled ? 1 : 0.72,
                            }}
                        >
                            <span style={{
                                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                                background: avatarColor(row.email), color: '#fff', fontWeight: 600, fontSize: 13,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}>{initialsOf(row.email)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#1f2a28' }}>{displayName(row.email)}</div>
                                <div style={{ fontSize: 13, color: '#5b6b68', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.email}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                <Switch
                                    color='primary'
                                    checked={!!row.enabled}
                                    onChange={(e) => toggle(index, e.target.checked)}
                                    disabled={!canManage || saving}
                                />
                                <span style={{ width: 64, fontSize: 13, color: row.enabled ? '#2f7d5b' : '#8a9795' }}>
                                    {row.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                                {canManage && (
                                    <Tooltip title='Remove recipient'>
                                        <span>
                                            <IconButton size='small' onClick={() => remove(index)} disabled={saving}>
                                                <CloseIcon fontSize='small' />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    ))
                )}

                {/* Footer note */}
                <div style={{ padding: '14px 20px', borderTop: '1px solid #eef1f0', fontSize: 12.5, color: '#8a9795', background: '#fafbfb' }}>
                    Disabled recipients stay on the list but receive nothing.
                </div>
            </Paper>

            {/* Notification history */}
            <Paper variant='outlined' style={{ overflow: 'hidden' }}>
                <div style={{ padding: 20 }}>
                    <h5 className='mb-0' style={{ fontWeight: 700 }}>Notification history</h5>
                    <div style={{ fontSize: 13, color: '#5b6b68', marginTop: 6 }}>
                        Every monitoring digest email sent for this client, newest first.
                    </div>
                </div>
                {auditLoading && !audit.length ? (
                    <div style={{ padding: 24, textAlign: 'center', borderTop: '1px solid #eef1f0' }}><CircularProgress size={22} /></div>
                ) : audit.length === 0 ? (
                    <div style={{ padding: 28, textAlign: 'center', color: '#5b6b68', borderTop: '1px solid #eef1f0' }}>
                        No notifications sent yet.
                    </div>
                ) : (
                    <>
                        {/* Column header */}
                        <div style={{
                            display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1.4fr', gap: 16,
                            padding: '10px 20px', borderTop: '1px solid #eef1f0', borderBottom: '1px solid #eef1f0',
                            fontSize: 12, letterSpacing: '.04em', color: '#8a9795', fontWeight: 600, textTransform: 'uppercase',
                        }}>
                            <span>Sent</span><span>Recipient</span><span>Business</span><span>Subject</span>
                        </div>
                        {auditGroups.map((group) => (
                            <div key={group.key}>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', padding: '8px 20px',
                                    background: '#fafbfb', borderBottom: '1px solid #f3f5f4',
                                    fontSize: 12, letterSpacing: '.04em', color: '#5b6b68', fontWeight: 700, textTransform: 'uppercase',
                                }}>
                                    <span>{group.label}</span>
                                    <span style={{ color: '#8a9795' }}>{group.items.length} email{group.items.length === 1 ? '' : 's'}</span>
                                </div>
                                {group.items.map((a, i) => (
                                    <div
                                        key={`${group.key}-${i}`}
                                        style={{
                                            display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1.4fr', gap: 16,
                                            padding: '12px 20px', borderBottom: '1px solid #f3f5f4', alignItems: 'center', fontSize: 13.5,
                                        }}
                                    >
                                        <span style={{ color: '#5b6b68', whiteSpace: 'nowrap' }}>
                                            {a.sentAt ? moment(a.sentAt).format('h:mm A') : '—'}
                                        </span>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.recipient || ''}>
                                            {a.recipient || '—'}
                                        </span>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {a.business && a.applicationId ? (
                                                <span
                                                    role='link'
                                                    tabIndex={0}
                                                    onClick={() => openApplicant(a.applicationId)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openApplicant(a.applicationId) }}
                                                    style={{ color: '#1f2a28', cursor: 'pointer', fontWeight: 700 }}
                                                >
                                                    {a.business}
                                                </span>
                                            ) : (a.business || '—')}
                                        </span>
                                        <span style={{ color: '#1f2a28' }}>{a.subject || '—'}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </Paper>
        </div>
    )
}

export default MonitoringNotifications
